import React, { useMemo, useState, useLayoutEffect, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import {
  arcDashed,
  sunriseIcon,
  sunsetIcon,
  sunHorizonIcon,
} from "../assets/dashboard-ui/icons.js";
import { formatTimeFromForecastIso } from "../services/forecastService";
import { glassCard } from "../styles/glass";

/** Sunrise / sunset marker positions in viewBox 0 0 320 150 — sun path matches these endpoints. */
const ARC_ENDPOINTS = {
  sunrise: { cx: -40, cy: 158 },
  sunset: { cx: 355, cy: 158 },
};

/** Dashed arc raster placement in viewBox 0 0 320 150 (tuned in browser). */
const ARC_DASHED_IMG = { x: -70, y: 30, width: 450, height: 200 };

/**
 * Pixels below dashed image top used as arc apex Y (centerline of stroke in art).
 * Nudge this (e.g. 10–24) until the sun sits on the dashed curve mid-day.
 */
const ARC_APEX_OFFSET_Y = 14;

/** Circumcircle through sunrise, sunset, and apex — smooth circular motion aligned to the PNG. */
function circumcenter(ax, ay, bx, by, cx, cy) {
  const d = 2 * (ax * (by - cy) + bx * (cy - ay) + cx * (ay - by));
  if (Math.abs(d) < 1e-6) return null;
  const a2 = ax * ax + ay * ay;
  const b2 = bx * bx + by * by;
  const c2 = cx * cx + cy * cy;
  const ux = (a2 * (by - cy) + b2 * (cy - ay) + c2 * (ay - by)) / d;
  const uy = (a2 * (cx - bx) + b2 * (ax - cx) + c2 * (bx - ax)) / d;
  const r = Math.hypot(ax - ux, ay - uy);
  return { cx: ux, cy: uy, r };
}

function buildSunArc() {
  const sx = ARC_ENDPOINTS.sunrise.cx;
  const sy = ARC_ENDPOINTS.sunrise.cy;
  const ex = ARC_ENDPOINTS.sunset.cx;
  const ey = ARC_ENDPOINTS.sunset.cy;
  const apexX = (sx + ex) / 2;
  const apexY = ARC_DASHED_IMG.y + ARC_APEX_OFFSET_Y;

  const c = circumcenter(sx, sy, ex, ey, apexX, apexY);
  if (!c) {
    const cx = (sx + ex) / 2;
    const cy = (sy + ey) / 2;
    const r = Math.hypot(ex - cx, ey - cy) || 1;
    return { cx, cy, r, mode: "diameter" };
  }
  const theta0 = Math.atan2(sy - c.cy, sx - c.cx);
  const theta1 = Math.atan2(ey - c.cy, ex - c.cx);
  return { ...c, theta0, theta1, mode: "fitted" };
}

const SUN_ARC = buildSunArc();

function clamp01(x) {
  if (Number.isNaN(x) || x == null) return 0;
  return Math.min(1, Math.max(0, x));
}

function sunDiscPosition(progress) {
  const p = clamp01(progress);

  if (SUN_ARC.mode === "diameter") {
    const { cx, cy, r } = SUN_ARC;
    const angle = Math.PI * (1 - p);
    return { x: cx + r * Math.cos(angle), y: cy - r * Math.sin(angle) };
  }

  const { cx, cy, r, theta0, theta1 } = SUN_ARC;
  const theta = theta0 + p * (theta1 - theta0);
  return { x: cx + r * Math.cos(theta), y: cy + r * Math.sin(theta) };
}

function easeOutCubic(t) {
  const u = clamp01(t);
  return 1 - (1 - u) ** 3;
}

function dayArcProgress(nowMs, sunrise, sunset) {
  if (!sunrise || !sunset) return 0.45;
  const sr = sunrise.getTime();
  const ss = sunset.getTime();
  if (ss <= sr) return 0.5;
  if (nowMs < sr) return 0;
  if (nowMs > ss) return 1;
  return (nowMs - sr) / (ss - sr);
}

/** Display clock in forecast location TZ (from API `profile.tz`), else from ISO, never hardcoded. */
function formatSunTime(date, timeZone, isoFallback) {
  if (
    timeZone &&
    date instanceof Date &&
    !Number.isNaN(date.getTime())
  ) {
    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZone,
    });
  }
  const fromIso = formatTimeFromForecastIso(isoFallback);
  if (fromIso) return fromIso;
  if (date instanceof Date && !Number.isNaN(date.getTime())) {
    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  }
  return "—";
}

function IconImg({ src, alt, className }) {
  return (
    <img
      src={src}
      alt={alt || ""}
      className={`shrink-0 object-contain ${className || ""}`}
    />
  );
}

export default function SunriseSunsetArc({
  sunrise,
  sunset,
  timeZone,
  sunriseIso,
  sunsetIso,
  reduceMotion,
  nowTick,
  glassCardClassName,
}) {
  /** null = live clock; number = intro eased progress along arc. */
  const [introArcProgress, setIntroArcProgress] = useState(null);
  const [animToken, setAnimToken] = useState(0);
  const introTargetRef = useRef(0);
  const skipIntroRef = useRef(true);

  const liveArcProgress = useMemo(
    () => dayArcProgress(nowTick, sunrise, sunset),
    [nowTick, sunrise, sunset]
  );

  useLayoutEffect(() => {
    if (!sunrise || !sunset || reduceMotion) {
      skipIntroRef.current = true;
      setIntroArcProgress(null);
      setAnimToken((t) => t + 1);
      return;
    }
    const target = dayArcProgress(Date.now(), sunrise, sunset);
    introTargetRef.current = target;
    const skip = target <= 1e-5;
    skipIntroRef.current = skip;
    setAnimToken((t) => t + 1);
    if (skip) {
      setIntroArcProgress(null);
      return;
    }
    setIntroArcProgress(0);
  }, [sunrise, sunset, reduceMotion]);

  useEffect(() => {
    if (reduceMotion || !sunrise || !sunset) return undefined;
    if (skipIntroRef.current) return undefined;

    const endP = introTargetRef.current;
    let cancelled = false;
    const rafRef = { id: 0 };
    const t0 = performance.now();
    const DUR = 2200;

    const frame = (now) => {
      if (cancelled) return;
      const u = Math.min(1, (now - t0) / DUR);
      const eased = easeOutCubic(u);
      setIntroArcProgress(eased * endP);
      if (u < 1) {
        rafRef.id = requestAnimationFrame(frame);
      } else {
        setIntroArcProgress(null);
      }
    };

    rafRef.id = requestAnimationFrame(frame);
    return () => {
      cancelled = true;
      cancelAnimationFrame(rafRef.id);
    };
  }, [animToken, sunrise, sunset, reduceMotion]);

  const arcProgress = useMemo(() => {
    if (!sunrise || !sunset) return liveArcProgress;
    if (introArcProgress === null) return liveArcProgress;
    return introArcProgress;
  }, [introArcProgress, liveArcProgress, sunrise, sunset]);

  const sunPos = useMemo(() => sunDiscPosition(arcProgress), [arcProgress]);

  const sunAnimating = introArcProgress !== null;

  return (
    <motion.div
      initial={reduceMotion ? false : { opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: reduceMotion ? 0 : 0.15, duration: 0.45, ease: "easeOut" }}
      className={`${glassCard} relative overflow-hidden ${glassCardClassName || ""}`}
    >
      <h3 className="text-base font-semibold tracking-tight text-white">
        Sunrise &amp; Sunset
      </h3>

      <div className="mx-auto mt-2 flex w-full max-w-[500px] flex-col items-stretch gap-1 sm:max-w-[520px]">
        <svg
          viewBox="0 0 320 150"
          className="mx-auto block h-[150px] w-full max-w-[500px] select-none sm:h-[170px] sm:max-w-[520px]"
          role="img"
          aria-label="Sun path from sunrise to sunset"
        >
          <defs>
            <linearGradient id="sunGlow" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#fbbf24" stopOpacity="0.9" />
              <stop offset="100%" stopColor="#f59e0b" stopOpacity="0" />
            </linearGradient>
            <filter id="sunGlowFilter" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="3" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          <image
            href={arcDashed}
            x={ARC_DASHED_IMG.x}
            y={ARC_DASHED_IMG.y}
            width={ARC_DASHED_IMG.width}
            height={ARC_DASHED_IMG.height}
            preserveAspectRatio="xMidYMid meet"
            opacity={0.95}
            style={{ filter: "brightness(1.22) contrast(1.06)" }}
            className="pointer-events-none"
          />

          <circle
            cx={ARC_ENDPOINTS.sunrise.cx}
            cy={ARC_ENDPOINTS.sunrise.cy}
            r="5"
            fill="#FCD34D"
            stroke="#FEF08A"
            strokeWidth="1.5"
            className="pointer-events-none"
          />
          <circle
            cx={ARC_ENDPOINTS.sunset.cx}
            cy={ARC_ENDPOINTS.sunset.cy}
            r="5"
            fill="#FB923C"
            stroke="#FDBA74"
            strokeWidth="1.5"
            className="pointer-events-none"
          />

          <g
            style={{
              transition:
                reduceMotion || sunAnimating
                  ? undefined
                  : "transform 0.35s cubic-bezier(0.22, 1, 0.36, 1)",
            }}
            transform={`translate(${sunPos.x}, ${sunPos.y})`}
          >
            <circle
              r="14"
              fill="url(#sunGlow)"
              filter="url(#sunGlowFilter)"
              className={
                reduceMotion ? "" : "animate-[pulse-sun_3s_ease-in-out_infinite]"
              }
            />
            <circle r="9" fill="#fbbf24" stroke="#fde68a" strokeWidth="1.5" />
          </g>
        </svg>

        <div className="grid w-full grid-cols-3 items-end gap-2 px-0 pt-1 sm:gap-3 sm:px-1">
          <div className="flex min-w-0 flex-col items-start gap-1.5">
            <IconImg
              src={sunriseIcon}
              alt=""
              className="h-10 w-10 shrink-0 drop-shadow-sm sm:h-11 sm:w-11"
            />
            <span className="text-sm font-medium text-white">Sunrise</span>
            <span className="text-xl font-bold leading-tight tracking-tight text-white sm:text-2xl">
              {formatSunTime(sunrise, timeZone, sunriseIso)}
            </span>
          </div>

          <div className="flex flex-col items-center justify-end pb-1">
            <IconImg
              src={sunHorizonIcon}
              alt=""
              className="h-[7.5rem] w-[7.5rem] shrink-0 object-contain opacity-95 brightness-0 invert sm:h-[8.25rem] sm:w-[8.25rem]"
            />
          </div>

          <div className="flex min-w-0 flex-col items-end gap-1.5 text-right">
            <IconImg
              src={sunsetIcon}
              alt=""
              className="h-10 w-10 shrink-0 drop-shadow-sm sm:h-11 sm:w-11"
            />
            <span className="text-sm font-medium text-white">Sunset</span>
            <span className="text-xl font-bold leading-tight tracking-tight text-white sm:text-2xl">
              {formatSunTime(sunset, timeZone, sunsetIso)}
            </span>
          </div>
        </div>
      </div>

      {(!sunrise || !sunset) && (
        <p className="mt-3 text-center text-[11px] leading-snug text-gray-400">
          Sun times unavailable for this location.
        </p>
      )}

      <style>{`
        @keyframes pulse-sun {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.82; }
        }
      `}</style>
    </motion.div>
  );
}
